/**
 * linkifyText — Web port of Flutter's `Linkify` widget for chat-bubble text.
 *
 * Splits an input string on http/https URLs and returns a React node list
 * where each URL span renders as a clickable anchor (opens in a new tab,
 * `target="_blank"` + `rel="noopener noreferrer"`). Non-URL spans render as
 * plain text. Empty / null / undefined input returns an empty array.
 *
 * Flutter source of truth (`chat_screen.dart:1438-1450`):
 *
 * ```dart
 * Linkify(
 *   onOpen: (link) async {
 *     Uri uri = Uri.parse(link.url);
 *     if (await canLaunchUrl(uri)) {
 *       await launchUrl(uri);
 *     } else {
 *       Fluttertoast.showToast(msg: 'Could not launch $link');
 *     }
 *   },
 *   text: chatMessage.content!,
 *   style: AppStyles.chatStyle,
 *   linkStyle: const TextStyle(color: AppColors.white),
 * ),
 * ```
 *
 * Web equivalence:
 *  - Anchor `href` is the matched URL verbatim — browser navigation handles
 *    the "can launch" check implicitly. There is no "could not launch" path
 *    on the web; the toast branch was Flutter-specific.
 *  - Anchors use `target="_blank"` so the host page (chat) is preserved, and
 *    `rel="noopener noreferrer"` for the standard security hygiene.
 *  - Visual styling of anchors (color, decoration) is the caller's job —
 *    each bubble component already owns its own text/link color via the
 *    theme. This util returns semantic anchors only; it does NOT inline any
 *    color or font. Anchor `color` and `text-decoration` inherit from the
 *    parent element via CSS inheritance — a caller wanting Flutter's
 *    `linkStyle` (white on own / peer bubbles) just sets `color` on the
 *    wrapping bubble container, and the browser propagates it to the
 *    anchor. That matches Flutter where `linkStyle` is passed in by the
 *    bubble, not by `Linkify` itself.
 *
 * URL match pattern: `/(https?:\/\/[^\s]+)/g` — http(s) schemes only, ended
 * by whitespace. This intentionally does NOT match bare-domain URLs (e.g.
 * `example.com` without `https://`) because Flutter's `Linkify` default
 * `LinkifierKit` covers the same surface in practice for chat content.
 *
 * Returned `key`s on the spans are stable for a given input string (they
 * use the segment index plus the segment role) — safe to reuse as the React
 * key.
 */

import React from 'react';

const URL_PATTERN = /(https?:\/\/[^\s]+)/g;

export interface LinkifyTextOptions {
  /**
   * Optional callback fired when the user clicks a link. The default browser
   * navigation (anchor `target="_blank"`) still happens; this is purely a
   * hook for analytics / logging. The matched URL is passed as the first
   * argument.
   */
  onLinkClick?: (url: string) => void;
}

/**
 * Returns an array of React nodes from `text`, splitting on URL boundaries.
 * URL spans become anchors; everything else is plain text.
 *
 * Pure / SSR-safe. Does NOT consume any hooks or context.
 */
export function linkifyText(
  text: string | null | undefined,
  options?: LinkifyTextOptions,
): React.ReactNode[] {
  if (!text) {
    return [];
  }

  const segments: React.ReactNode[] = [];
  let lastIndex = 0;
  let segmentIndex = 0;

  // Reset the regex's lastIndex defensively — required because URL_PATTERN
  // is a module-level `g`-flag RegExp instance reused across calls.
  URL_PATTERN.lastIndex = 0;

  let match: RegExpExecArray | null = URL_PATTERN.exec(text);
  while (match !== null) {
    const matchStart = match.index;
    const matchEnd = matchStart + match[0].length;

    if (matchStart > lastIndex) {
      segments.push(
        <React.Fragment key={`text-${segmentIndex}`}>
          {text.substring(lastIndex, matchStart)}
        </React.Fragment>,
      );
      segmentIndex += 1;
    }

    const url = match[0];
    segments.push(
      <a
        key={`link-${segmentIndex}`}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(event) => {
          // Don't let the click bubble up to bubble-level tap handlers
          // (timestamp toggle, retry). Browser still follows the link.
          event.stopPropagation();
          options?.onLinkClick?.(url);
        }}
      >
        {url}
      </a>,
    );
    segmentIndex += 1;

    lastIndex = matchEnd;
    match = URL_PATTERN.exec(text);
  }

  if (lastIndex < text.length) {
    segments.push(
      <React.Fragment key={`text-${segmentIndex}`}>
        {text.substring(lastIndex)}
      </React.Fragment>,
    );
  }

  return segments;
}
